<!--
  AvailabilityPanel: content of the "Verfügbarkeiten" field on every profile (Lehrkraft, Bewerber:in, Standort, Schüler:in,
  Partner-Cockpit): compact label filter "Alle · Kurse · …" (only labels that occur, colour dot like the timetable bands),
  "Terminabgleich aktiv" status line (switchable display toggle: real-time windows vs. saved ones, only when real-time
  availabilities are available; narrow columns collapse filter + status into one "… · Filter" line), mini week timeline
  (AvailabilityTimeline) and the muted footer "Zuletzt aktualisiert am … von <Name>". The name is only loaded when the
  viewer may read login users (else only the date); windows without updatedBy (older entries) show only the date.
  Data loading, permissions and edit dialogs stay in the parent (field, pencil, dialog).
  Props: stored (saved windows, source of updatedAt/updatedBy and of the fallback), realTime (Terminabgleich result,
  undefined = not available), loading, emptyText, resolveEditor (false: never load the name, e.g. partner cockpit)
-->
<template>
  <div :class="{ 'avp--open': open }" class="avp">
    <button v-if="labels.length > 1 || hasRealTime" :aria-expanded="String(open)" class="avp-toggle" type="button" @click="open = !open">
      {{ open ? 'Weniger' : collapsedText }}
      <v-icon x-small>{{ open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </button>
    <div v-if="labels.length > 1 || hasRealTime" class="avp-head">
      <div v-if="labels.length > 1" aria-label="Verfügbarkeiten filtern" class="avp-filter" role="group">
        <button :aria-pressed="String(activeFilter === 'all')" :class="{ 'avp-seg--on': activeFilter === 'all' }" class="avp-seg" type="button"
                @click="filter = 'all'">Alle
        </button>
        <button v-for="label in labelItems" :key="label.value" :aria-pressed="String(activeFilter === label.value)"
                :class="{ 'avp-seg--on': activeFilter === label.value }" class="avp-seg" type="button" v-tip="label.text" @click="filter = label.value">
          <span :style="{ background: label.color }" class="avp-seg__dot"/>{{ label.short }}
        </button>
      </div>
      <button v-if="hasRealTime" :aria-pressed="String(useRealTime)" :class="{ 'avp-sync--on': useRealTime }" class="avp-sync" type="button"
              v-tip="useRealTime ? 'Verfügbarkeiten werden mit Kursen, Schulungen usw. abgeglichen – klicken zum Ausschalten'
                : 'Hinterlegte Verfügbarkeiten ohne Abgleich – klicken, um mit Kursen, Schulungen usw. abzugleichen'"
              @click="useRealTime = !useRealTime">
        <v-icon x-small>{{ useRealTime ? 'mdi-calendar-check-outline' : 'mdi-calendar-remove-outline' }}</v-icon>
        {{ useRealTime ? 'Terminabgleich aktiv' : 'Terminabgleich aus' }}
      </button>
      <span v-if="loading" class="avp-loading">lädt …</span>
    </div>
    <availability-timeline v-if="filtered.length" :availabilities="filtered" :axis-from="shown" :filter="activeFilter"
                           :labels-used="labels.length > 0"/>
    <div v-else class="fld__value fld__value--muted">{{ emptyText }}</div>
    <div v-if="update" class="avp-foot">Zuletzt aktualisiert am {{ updateText }}<template v-if="editor"> von {{ editor }}</template></div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import moment from 'moment';
import {Types} from 'mongoose';
import {
  CardAvailability, defaultFilter, editorName, filterAvailabilities, LABEL_META, LabelFilter, LabelMeta, lastUpdate, presentLabels,
  validFilter,
} from './availabilityCard.helper';
import AvailabilityTimeline from './AvailabilityTimeline.vue';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {LoginUserFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/LoginUserFields';
import {EmployeeFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/EmployeeFields';
import {UserLoginHandlerApi} from '@/classes/api/user-login-handler.api';

/** Names per login user id for this page session (one request per editor, failures cached as '') */
const editorCache = new Map<string, Promise<string>>();

export default Vue.extend({
  components: {AvailabilityTimeline},
  props: {
    stored: {type: Array as () => CardAvailability[] | null, default: null},
    realTime: {type: Array as () => CardAvailability[] | undefined, default: undefined},
    loading: {type: Boolean, default: false},
    emptyText: {type: String, default: 'Aktuell nicht verfügbar'},
    resolveEditor: {type: Boolean, default: true},
  },
  data: () => ({
    filter: undefined as LabelFilter | undefined,
    useRealTime: true,
    /** Narrow columns: filter + Terminabgleich collapse into one summary line */
    open: false,
    editor: '',
  }),
  computed: {
    hasRealTime(): boolean {
      return Array.isArray(this.realTime);
    },
    shown(): CardAvailability[] {
      // While the Terminabgleich loads, the saved windows stay visible
      return this.hasRealTime && this.useRealTime && !this.loading ? this.realTime as CardAvailability[] : this.stored || [];
    },
    labels(): string[] {
      return presentLabels([...(this.stored || []), ...this.shown]);
    },
    labelItems(): LabelMeta[] {
      return LABEL_META.filter((meta) => this.labels.includes(meta.value));
    },
    activeFilter(): LabelFilter {
      const items = [...(this.stored || []), ...this.shown];
      return this.filter === undefined ? defaultFilter(items) : validFilter(this.filter, items);
    },
    filtered(): CardAvailability[] {
      return filterAvailabilities(this.shown, this.activeFilter);
    },
    update(): { date: Date; userId?: string } | undefined {
      return lastUpdate(this.stored);
    },
    collapsedText(): string {
      const label = LABEL_META.find((meta) => meta.value === this.activeFilter);
      const parts = [this.labels.length > 1 ? (label ? label.short : 'Alle') : ''];
      if (this.hasRealTime) {
        parts.push(this.useRealTime ? 'Terminabgleich aktiv' : 'Terminabgleich aus');
      }
      return [...parts.filter(Boolean), 'Filter'].join(' · ');
    },
    updateText(): string {
      return this.update ? moment(this.update.date).format('DD.MM.YYYY [um] HH:mm [Uhr]') : '';
    },
  },
  methods: {
    canReadEditor(userId: string): boolean {
      try {
        return this.$$getCrudEntity(CrudEntityTypes.LOGIN_USER, userId as unknown as Types.ObjectId).canRead(LoginUserFields.META);
      } catch (e) {
        return false;
      }
    },
    async loadEditor() {
      const userId = this.update?.userId;
      this.editor = '';
      if (!this.resolveEditor || !userId || !this.canReadEditor(userId)) {
        return;
      }
      if (!editorCache.has(userId)) {
        editorCache.set(userId, UserLoginHandlerApi.findById(userId as unknown as Types.ObjectId, {
          fields: [LoginUserFields.META, LoginUserFields.TYPE],
          populate: {meta: {fields: [EmployeeFields.FIRST_NAME, EmployeeFields.LAST_NAME]}},
        } as any, {handledStatuses: [403, 404]}).then(editorName).catch(() => ''));
      }
      const name = await (editorCache.get(userId) as Promise<string>);
      if (this.update?.userId === userId) {
        this.editor = name;
      }
    },
  },
  watch: {
    'update.userId': {
      immediate: true,
      handler() {
        this.loadEditor();
      },
    },
  },
});
</script>

<style scoped>
.avp {
  container-type: inline-size;
}

.avp-toggle {
  display: none;
  align-items: center;
  gap: 2px;
  margin: 2px 0;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 11px;
  color: rgba(15, 23, 42, .55);
  cursor: pointer;
}

.avp-toggle:hover {
  color: #1565C0;
}

.avp-toggle .v-icon {
  color: inherit;
}

@container (max-width: 340px) {
  .avp-toggle {
    display: inline-flex;
  }

  .avp:not(.avp--open) .avp-head {
    display: none;
  }

  .avp-seg {
    height: 20px;
    padding: 0 6px;
  }
}

.avp-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin: 2px 0 2px;
}

.avp-filter {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 2px;
  border-radius: 8px;
  background: rgba(15, 23, 42, .05);
}

.avp-seg {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: rgba(15, 23, 42, .65);
  font: inherit;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
}

.avp-seg:hover {
  color: rgba(15, 23, 42, .9);
}

.avp-seg:focus-visible, .avp-sync:focus-visible {
  outline: 2px solid #1976D2;
  outline-offset: 1px;
}

.avp-seg--on {
  background: #fff;
  color: rgba(15, 23, 42, .9);
  box-shadow: 0 1px 2px rgba(15, 23, 42, .12);
}

.avp-seg__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.avp-sync {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  background: none;
  color: rgba(15, 23, 42, .5);
  font: inherit;
  font-size: 11px;
  cursor: pointer;
}

.avp-sync .v-icon {
  color: inherit;
}

.avp-sync--on {
  color: #2E7D32;
}

.avp-loading {
  font-size: 11px;
  color: rgba(15, 23, 42, .45);
}

.avp-foot {
  margin-top: 6px;
  font-size: 11px;
  color: rgba(15, 23, 42, .5);
}

.theme--dark .avp-filter {
  background: rgba(255, 255, 255, .07);
}

.theme--dark .avp-seg {
  color: rgba(255, 255, 255, .65);
}

.theme--dark .avp-seg--on {
  background: rgba(255, 255, 255, .14);
  color: #fff;
  box-shadow: none;
}

.theme--dark .avp-sync, .theme--dark .avp-loading, .theme--dark .avp-foot, .theme--dark .avp-toggle {
  color: rgba(255, 255, 255, .5);
}

.theme--dark .avp-sync--on {
  color: #A5D6A7;
}
</style>
<style src="@/components/acquisitionList/ui/itemDetails.css"></style>
